/**
 * Issue count aligned with TripDetailScreen Issues tab (trailer–cargo, global inventory vs plan, route).
 */

import { inferRouteStructure } from './routeInference';
import { computeInsufficientItemsFromPlans } from './inventoryCheckLogic';
import { quantityPlansFromLpSolution } from './quantityCalculation';
import {
    buildIncompatibilityKeySet,
    buildTrailersPerEdge,
    collectTrailerCargoInfeasibilityIssues,
    incompatibilityPairsFromRows,
} from './trailerCargoBan';
import { getLpSolutionFromTrip, type DagEdgeRow, type LpSolutionBlob } from './tripLpDb';
import type { DagNodeRow } from './tripLpRequirements';

function parseEventDate(value: string | number | null | undefined): Date | null {
    if (value == null || value === '') return null;
    const d = new Date(value as string | number);
    return Number.isNaN(d.getTime()) ? null : d;
}

export type TripForIssuesCount = {
    lpSolution?: LpSolutionBlob | null;
    tripDagNodes?: Array<{ index: number; kind: string; eventId?: string | null; storageLocationId?: string | null }>;
    tripDagEdges?: Array<{ fromNodeIndex: number; toNodeIndex: number }>;
    events?: Array<{
        id: string;
        name?: string;
        startDate?: string | number | null;
        endDate?: string | number | null;
        packingListItems?: Array<{
            itemType?: { id: string; category?: { name: string } | null } | null;
            quantity?: number;
        }>;
    }>;
    sourceStorageLocation?: { id: string; name?: string } | null;
    destStorageLocation?: { id: string; name?: string } | null;
};

type TrailerIncompatibilityRow = {
    trailerItemType?: { id?: string } | null;
    cargoItemType?: { id?: string } | null;
};

type ItemTypeLike = { id: string; name?: string | null; category?: { name?: string } | null };

type ItemTypeWithLocationsLike = {
    id: string;
    itemLocations?: Array<{ quantity?: number; event?: { id: string } | null; storageLocation?: { id: string } | null }>;
};

/**
 * Same total as TripDetailScreen `issuesTabCount`.
 */
export function computeTripIssuesTabCount(
    trip: TripForIssuesCount | null | undefined,
    trailerCargoIncompatibilityRows: TrailerIncompatibilityRow[] | null | undefined,
    itemTypes: ItemTypeLike[] | undefined,
    itemTypesWithLocationsForAvailability: ItemTypeWithLocationsLike[] | undefined
): number {
    if (!trip) return 0;

    const eventsRaw = trip.events ?? [];
    const events = eventsRaw.slice().sort((a, b) => {
        const aStart = parseEventDate(a.startDate)?.getTime() ?? 0;
        const bStart = parseEventDate(b.startDate)?.getTime() ?? 0;
        return aStart - bStart;
    });

    const eventsMap = new Map<string, { id: string; name?: string; startDate?: Date | null; endDate?: Date | null }>();
    for (const e of events) {
        eventsMap.set(e.id, {
            id: e.id,
            name: e.name,
            startDate: parseEventDate(e.startDate),
            endDate: parseEventDate(e.endDate),
        });
    }

    const packingListsForInference = new Map<string, Array<{ itemType?: { id: string; category?: { name: string } | null } | null }>>();
    for (const e of events) {
        const items = (e.packingListItems ?? []).map(item => ({
            itemType: item.itemType
                ? { id: item.itemType.id, category: item.itemType.category ? { name: item.itemType.category.name } : null }
                : null,
        }));
        if (items.length > 0) packingListsForInference.set(e.id, items);
    }

    const sourceStorageLocationId = trip.sourceStorageLocation?.id ?? null;
    const destStorageLocationId = trip.destStorageLocation?.id ?? null;

    const routeStructure =
        events.length > 0
            ? inferRouteStructure(
                  [],
                  events.map(e => e.id),
                  eventsMap,
                  packingListsForInference,
                  sourceStorageLocationId ? [sourceStorageLocationId] : [],
                  destStorageLocationId ? [destStorageLocationId] : []
              )
            : null;

    let routeIssueCount = 0;
    if (routeStructure) {
        const warnings = routeStructure.warnings.filter(w => w.severity !== 'info');
        routeIssueCount = routeStructure.conflicts.length + warnings.length;
    }

    const dagNodesFromTrip = (
        (trip.tripDagNodes ?? []) as Array<{
            index: number;
            kind: 'source' | 'event' | 'dest';
            eventId?: string | null;
            storageLocationId?: string | null;
        }>
    )
        .slice()
        .sort((a, b) => a.index - b.index) as DagNodeRow[];

    const dagEdgesFromTrip: DagEdgeRow[] = (trip.tripDagEdges ?? []) as DagEdgeRow[];

    const incompatibilityKeysForTrip = buildIncompatibilityKeySet(incompatibilityPairsFromRows(trailerCargoIncompatibilityRows));

    const trailersPerEdgeForTrip =
        routeStructure && sourceStorageLocationId && destStorageLocationId && dagNodesFromTrip.length > 0
            ? buildTrailersPerEdge(routeStructure, dagNodesFromTrip, sourceStorageLocationId, destStorageLocationId)
            : null;

    const packingListsByEvent = new Map<string, Array<{ itemTypeId: string; quantity: number }>>();
    for (const e of events) {
        const items = (e.packingListItems ?? []).map(item => ({
            itemTypeId: item.itemType?.id ?? '',
            quantity: item.quantity ?? 0,
        }));
        if (items.length > 0) packingListsByEvent.set(e.id, items);
    }

    const sourceName = trip.sourceStorageLocation?.name ?? 'HQ';
    const destName = trip.destStorageLocation?.name ?? 'Destination';

    const getNodeLabel = (nodeId: string): string => {
        if (nodeId.startsWith('storage:source:')) {
            const storageId = nodeId.slice(15);
            return storageId === sourceStorageLocationId ? sourceName : (trip.sourceStorageLocation?.name ?? 'Storage');
        }
        if (nodeId.startsWith('storage:dest:')) {
            const storageId = nodeId.slice(14);
            return storageId === destStorageLocationId ? destName : (trip.destStorageLocation?.name ?? 'Storage');
        }
        if (nodeId.startsWith('storage:')) {
            const storageId = nodeId.slice(8);
            if (storageId === sourceStorageLocationId) return sourceName;
            if (storageId === destStorageLocationId) return destName;
            return 'Storage';
        }
        if (nodeId === 'hq' || nodeId === 'destination') return nodeId === 'hq' ? sourceName : destName;
        return events.find(ev => ev.id === nodeId)?.name ?? nodeId;
    };

    const getNodeLabelByIndex = (nodeIndex: number): string => {
        const node = dagNodesFromTrip[nodeIndex] as DagNodeRow & { storageLocationId?: string | null };
        if (!node) return '';
        const nodeId =
            node.kind === 'source'
                ? `storage:source:${node.storageLocationId ?? ''}`
                : node.kind === 'dest'
                  ? `storage:dest:${node.storageLocationId ?? ''}`
                  : (node.eventId ?? '');
        return getNodeLabel(nodeId);
    };

    const getItemTypeName = (id: string) => itemTypes?.find(t => t.id === id)?.name ?? id;

    let trailerCargoIssueCount = 0;
    if (trailersPerEdgeForTrip && dagNodesFromTrip.length > 0 && dagEdgesFromTrip.length > 0) {
        trailerCargoIssueCount = collectTrailerCargoInfeasibilityIssues({
            dagNodes: dagNodesFromTrip,
            edges: dagEdgesFromTrip,
            trailersPerEdge: trailersPerEdgeForTrip,
            incompatibilityKeys: incompatibilityKeysForTrip,
            packingListsByEvent,
            getCargoName: getItemTypeName,
            getTrailerName: getItemTypeName,
            getNodeLabel: getNodeLabelByIndex,
            getEventLabel: id => eventsMap.get(id)?.name ?? id,
        }).length;
    }

    const effectiveLpSolution = getLpSolutionFromTrip(trip);

    let insufficientCount = 0;
    if (
        routeStructure &&
        routeStructure.trailerPaths.size > 0 &&
        dagNodesFromTrip.length > 0 &&
        routeStructure.conflicts.length === 0 &&
        effectiveLpSolution &&
        Object.keys(effectiveLpSolution).length > 0
    ) {
        const nodeQuantitiesByItemType: Record<string, Array<{ nodeIndex: number; quantity: number }>> = {};
        const edgeFlowsByItemType: Record<string, Array<{ fromNodeIndex: number; toNodeIndex: number; value: number }>> = {};
        type SolEntry = {
            A: Array<{ nodeIndex: number; quantity: number }>;
            T: Array<{ fromNodeIndex: number; toNodeIndex: number; value: number }>;
        };
        for (const [itemTypeId, sol] of Object.entries(effectiveLpSolution) as Array<[string, SolEntry]>) {
            nodeQuantitiesByItemType[itemTypeId] = sol.A;
            edgeFlowsByItemType[itemTypeId] = sol.T;
        }
        const getEventName = (id: string) => eventsMap.get(id)?.name ?? id;
        const quantityPlans = quantityPlansFromLpSolution(
            dagNodesFromTrip as Array<{ index: number; kind: 'source' | 'event' | 'dest'; eventId?: string | null }>,
            routeStructure,
            nodeQuantitiesByItemType,
            edgeFlowsByItemType,
            getEventName
        );
        if (quantityPlans && quantityPlans.size > 0) {
            const allItemLocations: Array<{ itemType?: { id: string } | null; quantity: number }> = [];
            for (const itemType of itemTypesWithLocationsForAvailability ?? []) {
                for (const loc of itemType.itemLocations ?? []) {
                    allItemLocations.push({
                        itemType: { id: itemType.id },
                        quantity: loc.quantity ?? 0,
                    });
                }
            }
            const getAvailableGlobal = (itemTypeId: string): number =>
                allItemLocations.filter(loc => loc.itemType?.id === itemTypeId).reduce((sum, loc) => sum + loc.quantity, 0);

            const plansWithTotal = new Map(Array.from(quantityPlans.entries()).map(([id, p]) => [id, { totalNeeded: p.totalNeeded }]));
            insufficientCount = computeInsufficientItemsFromPlans(plansWithTotal, getAvailableGlobal).length;
        }
    }

    return trailerCargoIssueCount + insufficientCount + routeIssueCount;
}
